import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getDatasets, getReports, getUsage, deleteDataset, deleteReport, downloadReport, signOut } from '../lib/supabase';

const PLAN_LIMITS = { free: { uploads: 3, reports: 5 }, pro: { uploads: Infinity, reports: Infinity } };
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51TWnBdRz674pMjSUI5hijEfhuye73euCH3xzGSexnoBwOLOsOA8vxlsJPAg8tlV3bLnJlhezWoRSqdDpLqJHrYli00eYJcxmGo';
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://rscpzkjfmpfrmpjgmxtf.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export default function Dashboard() {
  const { profile, session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [reports,  setReports]  = useState([]);
  const [usage,    setUsage]    = useState({ uploads:0, reports:0 });
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('datasets'); // 'datasets'|'reports'|'profile'

  const userId = session?.user?.id;
  const plan = profile?.plan || 'free';
  const limits = PLAN_LIMITS[plan];

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [ds, rp, us] = await Promise.all([getDatasets(userId), getReports(userId), getUsage(userId)]);
      setDatasets(ds); setReports(rp); setUsage(us);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Detect return from Stripe checkout and refresh profile
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      if (refreshProfile) refreshProfile();
      setTimeout(() => load(), 1000);
    }
  }, [refreshProfile]);



  async function handleDeleteDataset(ds) {
    if (!window.confirm(`Delete "${ds.name}"? This cannot be undone.`)) return;
    await deleteDataset(ds.id, ds.file_path, userId);
    load();
  }

  async function handleDeleteReport(rp) {
    if (!window.confirm(`Delete "${rp.file_name}"?`)) return;
    await deleteReport(rp.id, rp.file_path, userId);
    load();
  }

  async function handleDownloadReport(rp) {
    const blob = await downloadReport(rp.file_path);
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = rp.file_name; a.click();
    URL.revokeObjectURL(url);
  }

  async function handleSignOut() { await signOut(); navigate('/'); }

  const canUpload = usage.uploads < limits.uploads;
  const canGenerate = usage.reports < limits.reports;

  return (
    <div style={S.page}>
      {/* Sidebar */}
      <nav style={S.sidebar}>
        <div style={S.sideTop}>
          <div style={S.logo}>PMD</div>
          <div style={S.logoText}>ProcessMD</div>
        </div>

        <div style={S.navLinks}>
          {[['datasets','📁','Datasets'],['reports','📄','Reports'],['training','🎓','Training'],['profile','👤','Profile']].map(([id,icon,label])=>(
            <button key={id} style={{...S.navBtn, background:tab===id?'#0d2244':'transparent', color:tab===id?'#93c5fd':'#475569', border:tab===id?'1px solid #1e3a5f':'1px solid transparent'}} onClick={()=>setTab(id)}>
              <span style={{fontSize:16}}>{icon}</span>{label}
            </button>
          ))}
        </div>

        <div style={S.sideBottom}>
          {/* Usage bar */}
          <div style={S.usageCard}>
            <div style={{fontSize:10,color:'#4a6090',fontFamily:"'IBM Plex Mono',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:8}}>This month</div>
            <UsageBar label="Uploads" used={usage.uploads} limit={limits.uploads}/>
            <UsageBar label="Reports" used={usage.reports} limit={limits.reports}/>
            {plan==='free' && <button style={S.upgradeBtn} onClick={()=>setTab('profile')}>↑ Upgrade to Pro</button>}
          </div>
          <button style={S.signoutBtn} onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      {/* Main */}
      <main style={S.main}>
        {/* Header */}
        <div style={S.mainHeader}>
          <div>
            <div style={S.greeting}>Hello, {profile?.full_name?.split(' ')[0] || 'there'} 👋</div>
            <div style={S.greetingSub}>{profile?.company || 'ProcessMD Portal'}</div>
          </div>
          {tab === 'datasets' && (
            <>
              <a href="/help.html" target="_blank" style={{...S.primaryBtn, background:'transparent', border:'1px solid #1e3a5f', color:'#64748b', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, marginRight:8, fontSize:12}}>
                ❓ Data Guide
              </a>
              <a href="/onepager.html" target="_blank" style={{...S.primaryBtn, background:'transparent', border:'1px solid #1e3a5f', color:'#64748b', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, marginRight:8, fontSize:12}}>
                📄 One-Pager
              </a>
              <button style={{...S.primaryBtn, opacity:canUpload?1:0.5, cursor:canUpload?'pointer':'not-allowed'}}
                disabled={!canUpload} onClick={()=>navigate('/analyse')}>
                + New Analysis
              </button>
            </>
          )}
        </div>

        {loading ? <Spinner/> : (
          <>
            {tab === 'datasets' && <DatasetsTab datasets={datasets} onDelete={handleDeleteDataset} onOpen={ds=>navigate('/analyse', {state:{datasetId:ds.id, filePath:ds.file_path, name:ds.name}})}/>}
            {tab === 'reports'  && <ReportsTab reports={reports} onDelete={handleDeleteReport} onDownload={handleDownloadReport}/>}
            {tab === 'profile'  && <ProfileTab profile={profile} plan={plan} onSaved={load}/>}
            {tab === 'training' && <TrainingTab/>}
          </>
        )}
      </main>
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const pct = limit === Infinity ? 0 : Math.min(100, (used/limit)*100);
  const over = pct > 80;
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#64748b',marginBottom:4}}>
        <span>{label}</span>
        <span style={{color:over?'#ef4444':'#64748b'}}>{used}/{limit===Infinity?'∞':limit}</span>
      </div>
      <div style={{height:4,background:'#0d1a2e',borderRadius:2}}>
        {limit!==Infinity&&<div style={{height:4,width:`${pct}%`,background:over?'#ef4444':'#3b82f6',borderRadius:2,transition:'width .3s'}}/>}
      </div>
    </div>
  );
}

function DatasetsTab({ datasets, onDelete, onOpen }) {
  if (!datasets.length) return (
    <div style={S.empty}>
      <div style={{fontSize:48,marginBottom:16}}>📂</div>
      <div style={{fontSize:16,fontWeight:600,color:'#cbd5e1',marginBottom:8}}>No datasets yet</div>
      <div style={{fontSize:13,color:'#4a6090'}}>Click "New Analysis" to upload your first process event log</div>
    </div>
  );
  return (
    <div>
      <div style={S.sectionTitle}>Your Datasets</div>
      <div style={S.grid}>
        {datasets.map(ds=>(
          <div key={ds.id} style={S.card}>
            <div style={S.cardIcon}>📊</div>
            <div style={S.cardName}>{ds.name}</div>
            <div style={S.cardMeta}>{ds.row_count?.toLocaleString()} events · {ds.case_count} cases</div>
            <div style={S.cardDate}>{new Date(ds.uploaded_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
            <div style={S.cardActions}>
              <button style={S.actionBtn} onClick={()=>onOpen(ds)}>Open →</button>
              <button style={{...S.actionBtn,...S.dangerBtn}} onClick={()=>onDelete(ds)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab({ reports, onDelete, onDownload }) {
  const icons = { word:'📝', pptx:'📊', excel:'📈' };
  if (!reports.length) return (
    <div style={S.empty}>
      <div style={{fontSize:48,marginBottom:16}}>📄</div>
      <div style={{fontSize:16,fontWeight:600,color:'#cbd5e1',marginBottom:8}}>No reports yet</div>
      <div style={{fontSize:13,color:'#4a6090'}}>Generate your first report from an analysis</div>
    </div>
  );
  return (
    <div>
      <div style={S.sectionTitle}>Generated Reports</div>
      <div style={S.grid}>
        {reports.map(rp=>(
          <div key={rp.id} style={S.card}>
            <div style={S.cardIcon}>{icons[rp.type]||'📄'}</div>
            <div style={S.cardName}>{rp.file_name}</div>
            <div style={S.cardMeta}>From: {rp.datasets?.name || 'Deleted dataset'}</div>
            <div style={S.cardDate}>{new Date(rp.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
            {rp.expires_at && <div style={{...S.cardDate,color:'#64748b'}}>Expires {new Date(rp.expires_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</div>}
            <div style={S.cardActions}>
              <button style={S.actionBtn} onClick={()=>onDownload(rp)}>⬇ Download</button>
              <button style={{...S.actionBtn,...S.dangerBtn}} onClick={()=>onDelete(rp)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileTab({ profile, plan, onSaved }) {
  const { refreshProfile, session } = useAuth();
  const [upgrading, setUpgrading] = useState(false);

  async function handleUpgrade() {
    try {
      setUpgrading(true);
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          successUrl: window.location.origin + '/?upgraded=true',
          cancelUrl: window.location.origin + '/',
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error: ' + (data.error || 'Could not create checkout session'));
        setUpgrading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error connecting to payment service: ' + err.message);
      setUpgrading(false);
    }
  }

  const [name, setName]     = useState(profile?.full_name || '');
  const [company, setCompany] = useState(profile?.company || '');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      const { updateProfile } = await import('../lib/supabase');
      await updateProfile(session.user.id, { full_name:name, company });
      await refreshProfile();
      setSaved(true); setTimeout(()=>setSaved(false), 2000);
      onSaved();
    } catch(err) { alert(err.message); }
    setSaving(false);
  }

  return (
    <div style={{maxWidth:520}}>
      <div style={S.sectionTitle}>Profile & Account</div>
      <div style={{background:'#0a1525',border:'1px solid #1e2d4a',borderRadius:12,padding:28,marginBottom:20}}>
        <form onSubmit={save}>
          <label style={S.formLabel}>Full name</label>
          <input style={S.formInput} value={name} onChange={e=>setName(e.target.value)}/>
          <label style={S.formLabel}>Company</label>
          <input style={S.formInput} value={company} onChange={e=>setCompany(e.target.value)}/>
          <label style={S.formLabel}>Email</label>
          <input style={{...S.formInput,opacity:0.5}} value={session?.user?.email||''} disabled/>
          <button style={{...S.primaryBtn,marginTop:8,opacity:saving?0.6:1}} disabled={saving}>
            {saving?'Saving…':saved?'✓ Saved':'Save changes'}
          </button>
        </form>
      </div>

      <div style={{background:'#0a1525',border:'1px solid #1e2d4a',borderRadius:12,padding:28}}>
        <div style={{fontSize:14,fontWeight:700,color:'#e2e8f0',marginBottom:4}}>Current plan: <span style={{color:plan==='pro'?'#fde68a':'#3b82f6',textTransform:'uppercase'}}>{plan}</span></div>
        {plan==='free' ? <>
          <div style={{fontSize:13,color:'#64748b',marginBottom:16,lineHeight:1.6}}>Free plan: 3 uploads/month · 5 reports/month · All 10 analytics</div>
          <div style={{background:'linear-gradient(135deg,#1B4F8A,#0D1B3E)',borderRadius:10,padding:20,marginBottom:8}}>
            <div style={{fontSize:15,fontWeight:700,color:'#fde68a',marginBottom:8}}>Pro Plan — $199/month</div>
            {['Unlimited uploads and reports','Word & PowerPoint generation','Priority report generation','Email support'].map((f,i)=>(
              <div key={i} style={{fontSize:13,color:'#93c5fd',marginBottom:4}}>✓ {f}</div>
            ))}
            <button style={{...S.primaryBtn,background:'#D4A017',marginTop:16,color:'#0D1B3E',fontWeight:800}}
              disabled={upgrading}
              onClick={handleUpgrade}>
              {upgrading ? 'Redirecting to checkout...' : '↑ Upgrade to Pro — $199/month'}
            </button>
          </div>
        </> : <div style={{fontSize:13,color:'#86efac'}}>✓ Pro plan active — unlimited uploads and reports</div>}
      </div>
    </div>
  );
}

function TrainingTab() {
  return (
    <div style={{padding:'0 2px'}}>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:700,color:'#e2e8f0',marginBottom:6}}>Training</div>
        <div style={{fontSize:13,color:'#64748b'}}>Learn how to use process data to diagnose and improve your operations.</div>
      </div>

      {/* Video */}
      <div style={{background:'#040c18',border:'1px solid #1e2d4a',borderRadius:12,overflow:'hidden',marginBottom:24}}>
        <div style={{position:'relative',paddingBottom:'56.25%',height:0}}>
          <iframe
            src="https://www.youtube.com/embed/QkTmbWX1krk"
            title="ProcessMD Training — Understanding Process Data, Metrics & Insights"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}}
          />
        </div>
        <div style={{padding:'16px 20px',borderTop:'1px solid #1e2d4a'}}>
          <div style={{fontSize:14,fontWeight:700,color:'#e2e8f0',marginBottom:4}}>Understanding Process Data, Metrics & Insights</div>
          <div style={{fontSize:12,color:'#64748b',lineHeight:1.6}}>A complete walkthrough of process analytics — event logs, cycle time, PCE, VA/BVA/NVA classification, resource utilisation, and how to turn data into operational recommendations.</div>
        </div>
      </div>

      {/* Resources */}
      <div style={{fontSize:13,fontWeight:600,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:12}}>Supporting Materials</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
        {[
          { icon:'📊', title:'Training Slide Deck', desc:'14-slide PowerPoint covering all process analytics concepts', href:'#', label:'Download PPTX' },
          { icon:'🎤', title:'Speaker Talk Track', desc:'Full word-for-word talk track for each slide with delivery tips', href:'#', label:'Download DOCX' },
          { icon:'📄', title:'Product One-Pager', desc:'One-page marketing overview of ProcessMD — share with colleagues', href:'/onepager.html', label:'View / Print PDF' },
        ].map(({ icon, title, desc, href, label }) => (
          <div key={title} style={{background:'#040c18',border:'1px solid #1e2d4a',borderRadius:10,padding:'16px 18px'}}>
            <div style={{fontSize:22,marginBottom:8}}>{icon}</div>
            <div style={{fontSize:13,fontWeight:700,color:'#e2e8f0',marginBottom:4}}>{title}</div>
            <div style={{fontSize:11,color:'#64748b',lineHeight:1.5,marginBottom:12}}>{desc}</div>
            <a href={href} target="_blank" rel="noreferrer"
              style={{fontSize:11,fontWeight:700,color:'#3b82f6',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4}}>
              {label} →
            </a>
          </div>
        ))}
      </div>

      {/* Data guide link */}
      <div style={{marginTop:20,padding:'14px 18px',background:'#040c18',border:'1px solid #1e2d4a',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:'#e2e8f0',marginBottom:3}}>❓ Data Format Guide</div>
          <div style={{fontSize:11,color:'#64748b'}}>Step-by-step guide to formatting your event log for ProcessMD — with templates and examples.</div>
        </div>
        <a href="/help.html" target="_blank" rel="noreferrer"
          style={{fontSize:12,fontWeight:700,color:'#fff',background:'#1e3a5f',border:'1px solid #2d5a8e',borderRadius:8,padding:'8px 16px',textDecoration:'none',whiteSpace:'nowrap',marginLeft:16}}>
          Open Guide →
        </a>
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{display:'flex',justifyContent:'center',paddingTop:60}}><div style={{width:32,height:32,border:'3px solid #1e2d4a',borderTop:'3px solid #3b82f6',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>;
}

const V = { bg:'#040c18', sidebar:'#03070f', border:'#0f1a2e', text:'#e2e8f0', muted:'#64748b', accent:'#3b82f6', gold:'#D4A017', navy:'#0D1B3E' };

const S = {
  page:       { display:'flex', minHeight:'100vh', background:V.bg, fontFamily:"'DM Sans',system-ui,sans-serif", color:V.text },
  sidebar:    { width:220, background:V.sidebar, borderRight:`1px solid ${V.border}`, display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh' },
  sideTop:    { padding:'22px 16px 14px', display:'flex', alignItems:'center', gap:10, borderBottom:`1px solid ${V.border}` },
  logo:       { width:32, height:32, background:V.gold, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:V.navy, fontFamily:"'IBM Plex Mono',monospace", flexShrink:0 },
  logoText:   { fontSize:14, fontWeight:700, letterSpacing:'-0.02em' },
  navLinks:   { padding:'12px 8px', flex:1, display:'flex', flexDirection:'column', gap:3 },
  navBtn:     { border:'none', cursor:'pointer', padding:'9px 12px', borderRadius:7, fontSize:12.5, fontWeight:500, display:'flex', alignItems:'center', gap:8, textAlign:'left', fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .15s' },
  sideBottom: { padding:'12px 12px 20px', borderTop:`1px solid ${V.border}` },
  usageCard:  { background:'#070f1c', borderRadius:8, padding:'12px 14px', marginBottom:10 },
  upgradeBtn: { width:'100%', background:V.gold, color:V.navy, border:'none', borderRadius:6, padding:'7px 0', fontSize:11.5, fontWeight:700, cursor:'pointer', marginTop:8 },
  signoutBtn: { width:'100%', background:'transparent', color:'#334155', border:'1px solid #0f1a2e', borderRadius:6, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif" },
  main:       { flex:1, padding:'28px 32px', overflowY:'auto' },
  mainHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 },
  greeting:   { fontSize:22, fontWeight:700, letterSpacing:'-0.02em' },
  greetingSub:{ fontSize:12, color:V.muted, marginTop:2 },
  primaryBtn: { background:V.accent, color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif" },
  sectionTitle:{ fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#334155', fontFamily:"'IBM Plex Mono',monospace", marginBottom:16, paddingLeft:2 },
  grid:       { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 },
  card:       { background:'#0a1525', border:'1px solid #1e2d4a', borderRadius:10, padding:'18px 18px 14px', display:'flex', flexDirection:'column', gap:4 },
  cardIcon:   { fontSize:24, marginBottom:4 },
  cardName:   { fontSize:13.5, fontWeight:600, color:'#cbd5e1', lineHeight:1.3 },
  cardMeta:   { fontSize:11, color:'#475569', fontFamily:"'IBM Plex Mono',monospace" },
  cardDate:   { fontSize:11, color:'#334155' },
  cardActions:{ display:'flex', gap:8, marginTop:10 },
  actionBtn:  { flex:1, background:'#0d1a2e', color:'#3b82f6', border:'1px solid #1e3a5f', borderRadius:6, padding:'6px 0', fontSize:11.5, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif" },
  dangerBtn:  { color:'#ef4444', border:'1px solid #450a0a', background:'#150404' },
  empty:      { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', paddingTop:60, textAlign:'center' },
  formLabel:  { display:'block', fontSize:11, fontWeight:600, color:'#94a3b8', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:6 },
  formInput:  { display:'block', width:'100%', background:'#070f1c', border:'1px solid #1e2d4a', borderRadius:8, padding:'10px 12px', fontSize:13, color:V.text, marginBottom:14, outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans',system-ui,sans-serif" },
};
